// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import { DataTablePresentationProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import { GridItemProps } from "@insite/mobius/GridItem";
import { DataTable, DataTableHead, DataTableHeader } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@tps/App/Components/Notify";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@tps/Overrides/Components/QuoteSummaryCard";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

// XNG-Change: add fields to the widget
const enum fields {
    widgetTitle = "WidgetTitle",
    numberOfRecords = "numberOfRecords",
    showRequestedDate = "showRequestedDate",
    showQuoteNumber = "showQuoteNumber",
    showPONumber = "showPONumber",
    showStatus = "showStatus",
    showExpirationDate = "showExpirationDate",
    showQuoteType = "showQuoteType",
    showCustomer = "showCustomer",
    showShipToPickUp = "showShipToPickUp",
    viewAllLinkText = "viewAllLinkText",
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        // XNG-Changes
        rfqEnabled: (getSettingsCollection(state).rfqSetting as any)?.enabled,
        rfqQuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

// XNG-Changes
interface OwnProps extends WidgetProps {
    fields: {
        [fields.widgetTitle]: string;
        [fields.numberOfRecords]: number;
        [fields.showRequestedDate]: boolean;
        [fields.showQuoteNumber]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showStatus]: boolean;
        [fields.showExpirationDate]: boolean;
        [fields.showQuoteType]: boolean;
        [fields.showCustomer]: boolean;
        [fields.showShipToPickUp]: boolean;
        [fields.viewAllLinkText]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    // XNG-Changes:
    container?: InjectableCss;
    headingWrapper?: InjectableCss;
    widgetTitle?: TypographyPresentationProps;
    viewAllLink?: LinkPresentationProps;
    quotesContainer?: InjectableCss;
    noResults?: InjectableCss;
    dataTable?: DataTablePresentationProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeader?: DataTableHeaderPresentationProps;
    dataTableBody?: DataTableBodyProps;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: { width: 12 },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        // XNG-Changes: update number of records retrived
        if (this.props.fields.numberOfRecords) {
            recentQuotesParameter.pageSize = this.props.fields.numberOfRecords;
        }
        // XNG-Change
        if (this.props.canViewQuotes && this.props.rfqEnabled) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const {
            settingsCollection,
            session,
            quotesDataView,
            myQuotesPageNavLink,
            canViewQuotes,
            fields,
            // XNG-Changes             
            rfqQuoteDetailsPageUrl,
        } = this.props;
        if (!canViewQuotes || !quotesDataView.value || !this.props.rfqEnabled) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;
        // XNG-Changes: Change Structure
        return (
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.headingWrapper}>
                    <Typography {...styles.widgetTitle}>{fields.WidgetTitle}</Typography>
                    <Link {...styles.viewAllLink} href={myQuotesPageUrl}>
                        {fields.viewAllLinkText}
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.quotesContainer}>
                    {quotesDataView.value.length === 0 && (
                        <Notify type="info" {...styles.noResults}>
                            {siteMessage("Rfq_NoQuotesMessage")}
                        </Notify>
                    )}
                    {quotesDataView.value.length !== 0 && (
                        <DataTable data-test-selector="recentQuotes" {...styles.dataTable}>
                            <DataTableHead {...styles.dataTableHead}>
                                {fields.showRequestedDate && (
                                    <DataTableHeader tight title={translate("Date")} {...styles.dataTableHeader}>
                                        {translate("Date")}
                                    </DataTableHeader>
                                )}
                                {fields.showQuoteNumber && (
                                    <DataTableHeader tight title={translate("Quote #")} {...styles.dataTableHeader}>
                                        {translate("Quote #")}
                                    </DataTableHeader>
                                )}
                                {fields.showPONumber && (
                                    <DataTableHeader tight title={translate("PO #")} {...styles.dataTableHeader}>
                                        {translate("PO #")}
                                    </DataTableHeader>
                                )}
                                {fields.showStatus && (
                                    <DataTableHeader tight title={translate("Status")} {...styles.dataTableHeader}>
                                        {translate("Status")}
                                    </DataTableHeader>
                                )}
                                {fields.showExpirationDate && (
                                    <DataTableHeader tight title={translate("Expires")} {...styles.dataTableHeader}>
                                        {translate("Expires")}
                                    </DataTableHeader>
                                )}
                                {fields.showQuoteType && settingsCollection.quoteSettings.jobQuoteEnabled && (
                                    <DataTableHeader tight title={translate("Type")} {...styles.dataTableHeader}>
                                        {translate("Type")}
                                    </DataTableHeader>
                                )}
                                {fields.showCustomer && (
                                    <DataTableHeader tight title={translate("Customer")} {...styles.dataTableHeader}>
                                        {translate("Customer")}
                                    </DataTableHeader>
                                )}
                                {fields.showShipToPickUp && (
                                    <DataTableHeader
                                        tight
                                        title={translate("Ship To/Pick Up")}
                                        {...styles.dataTableHeader}
                                    >
                                        {translate("Ship To/Pick Up")}
                                    </DataTableHeader>
                                )}
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {quotesDataView.value.map(quote => {
                                    return (
                                        <QuoteSummaryCard
                                            key={quote.id}
                                            quote={quote}
                                            session={session}
                                            quoteSettings={settingsCollection.quoteSettings}
                                            showRequestedDate={fields.showRequestedDate}
                                            showQuoteNumber={fields.showQuoteNumber}
                                            showPONumber={fields.showPONumber}
                                            showStatus={fields.showStatus}
                                            showExpirationDate={fields.showExpirationDate}
                                            showQuoteType={fields.showQuoteType}
                                            showCustomer={fields.showCustomer}
                                            showShipToPickUp={fields.showShipToPickUp}
                                            // XNG-Changes
                                            rfqQuoteDetailsPageUrl={rfqQuoteDetailsPageUrl}
                                        />
                                    );
                                })}
                            </DataTableBody>
                        </DataTable>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

// XNG-Changes
const fieldDefinitions: FieldDefinition[] = [
    {
        name: fields.widgetTitle,
        fieldType: "Contextual",
        displayName: translate("Title"),
        editorTemplate: "TextField",
        defaultValue: "Active Quotes",
    },
    {
        name: fields.viewAllLinkText,
        fieldType: "Contextual",
        displayName: translate("View All link text"),
        editorTemplate: "TextField",
        defaultValue: "View All Open Quotes",
    },
    {
        name: fields.numberOfRecords,
        displayName: "Number of Recent Quotes Displayed",
        editorTemplate: "IntegerField",
        min: 1,
        defaultValue: 5,
        fieldType: "General",
    },
    {
        name: fields.showRequestedDate,
        fieldType: "General",
        displayName: translate("Show Requested Date"),
        editorTemplate: "CheckboxField",
        defaultValue: true,
    },
    {
        name: fields.showQuoteNumber,
        fieldType: "General",
        displayName: translate("Show Quote #"),
        editorTemplate: "CheckboxField",
        defaultValue: true,
    },
    {
        name: fields.showPONumber,
        fieldType: "General",
        displayName: translate("Show PO #"),
        editorTemplate: "CheckboxField",
        defaultValue: true,
    },
    {
        name: fields.showStatus,
        fieldType: "General",
        displayName: translate("Show Status"),
        editorTemplate: "CheckboxField",
        defaultValue: true,
    },
    {
        name: fields.showExpirationDate,
        fieldType: "General",
        displayName: translate("Show Expiration Date"),
        editorTemplate: "CheckboxField",
        defaultValue: true,
    },
    {
        name: fields.showQuoteType,
        fieldType: "General",
        displayName: translate("Show Quote Type"),
        editorTemplate: "CheckboxField",
        defaultValue: true,
    },
    {
        name: fields.showCustomer,
        fieldType: "General",
        displayName: translate("Show Customer"),
        editorTemplate: "CheckboxField",
        defaultValue: true,
    },
    {
        name: fields.showShipToPickUp,
        fieldType: "General",
        displayName: translate("Show Ship To / Pick Up"),
        editorTemplate: "CheckboxField",
        defaultValue: true,
    },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions,
    },
};

export default widgetModule;
